// frontend/src/api/analytics.ts
import { api } from "./client.js";

export interface KeywordPerformance {
  keyword_id: number; keyword_text: string; campaign_id: number; ad_group_id: number;
  cost_rub: number; clicks: number; impressions: number;
  ctr: number | null; cpc: number | null;
  attributed_purchases: number; attributed_revenue: number; attributed_buyouts: number;
  roas: number | null; net_roas: number | null;
  cpl_pure: number | null; cpl_buyout: number | null;
}

export interface AdPerformance {
  ad_id: number; ad_title: string | null; ad_text: string | null;
  campaign_id: number; ad_group_id: number;
  cost_rub: number; clicks: number; impressions: number;
  ctr: number | null; cpc: number | null;
  attributed_purchases: number; attributed_revenue: number; attributed_buyouts: number;
  roas: number | null; net_roas: number | null;
  cpl_pure: number | null; cpl_buyout: number | null;
}

export interface SearchQueryPerformance {
  query: string; ad_group_id: number; campaign_id: number;
  cost_rub: number; clicks: number; impressions: number; conversions: number;
  ctr: number | null; cpc: number | null; cr: number | null; cpl: number | null;
  is_negative_candidate: boolean;
}

export interface SKUPerformance {
  product_id: string; name: string; price: number | null;
  impressions: number; baskets: number; purchases: number; revenue: number;
  basket_to_purchase_cr: number | null; view_to_purchase_cr: number | null;
}

export interface SKUByCampaign {
  campaign_id: number; campaign_name: string;
  product_id: string; product_name: string; product_price: number | null;
  attributed_qty: number; attributed_revenue: number;
  share_of_product_total: number | null;
}

export interface FunnelStep {
  goal_id: number; goal_name: string; reaches: number;
  drop_off_pct: number | null; step_index: number;
}

export interface AudienceRow {
  segment: string; visits: number; purchases: number; buyouts: number;
  revenue: number; buyout_rate: number | null;
  bounce_rate: number | null; avg_visit_duration: number | null; avg_page_views: number | null;
}

export interface LandingPerformance {
  landing_url: string; visits: number; purchases: number; buyouts: number;
  revenue: number; bounce_rate: number | null; avg_visit_duration: number | null; avg_page_views: number | null;
}

export interface HourPattern {
  hour: number; day_of_week: number;
  visits: number; purchases: number; revenue: number;
  buyout_rate: number | null; cr: number | null;
}

export interface BuyoutRate {
  scope: "global" | "category"; category: string | null;
  buyouts: number; orders: number; buyout_rate: number | null; period_days: number;
}

export const fetchKeywords = (campaignId: number | null, days: number) =>
  api<KeywordPerformance[]>(`/api/analytics/keywords?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ""}`);

export const fetchAds = (campaignId: number | null, days: number) =>
  api<AdPerformance[]>(`/api/analytics/ads?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ""}`);

export const fetchSearchQueries = (campaignId: number | null, days: number) =>
  api<SearchQueryPerformance[]>(`/api/analytics/search-queries?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ""}`);

export const fetchSKUs = (days: number) =>
  api<SKUPerformance[]>(`/api/analytics/skus?days=${days}`);

export const fetchSKU = (productId: string, days: number) =>
  api<SKUPerformance>(`/api/analytics/sku/${productId}?days=${days}`);

export const fetchSKUByCampaign = (campaignId: number, days: number) =>
  api<SKUByCampaign[]>(`/api/analytics/sku-by-campaign?campaign_id=${campaignId}&days=${days}`);

export const fetchFunnel = (campaignId: number | null, days: number) =>
  api<FunnelStep[]>(`/api/analytics/funnel?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ""}`);

export type AudienceDimensionKey =
  | "gender_age" | "interest" | "user_visits" | "days_since_first_visit"
  | "region_country" | "region_area" | "region_city"
  | "day_of_week" | "hour_of_day"
  | "device_category" | "os_root" | "operating_system" | "browser" | "browser_language" | "screen_format"
  | "last_traffic_source" | "last_search_engine" | "last_search_phrase" | "last_social_network"
  | "last_adv_engine" | "last_direct_click_order_name" | "last_referal_source"
  | "start_url" | "end_url" | "referer"
  | "device" | "region" | "os"; // legacy aliases

export interface AudienceDimensionOption {
  key: AudienceDimensionKey;
  label: string;
}

export const AUDIENCE_DIMENSION_GROUPS: Array<{ group: string; items: AudienceDimensionOption[] }> = [
  {
    group: "Кто",
    items: [
      { key: "gender_age", label: "Пол × Возраст" },
      { key: "interest", label: "Интересы" },
      { key: "user_visits", label: "Количество визитов" },
      { key: "days_since_first_visit", label: "Дней с первого визита" },
    ],
  },
  {
    group: "География",
    items: [
      { key: "region_country", label: "Страна" },
      { key: "region_area", label: "Область / край" },
      { key: "region_city", label: "Город" },
    ],
  },
  {
    group: "Когда",
    items: [
      { key: "day_of_week", label: "День недели" },
      { key: "hour_of_day", label: "Час суток" },
    ],
  },
  {
    group: "Устройство",
    items: [
      { key: "device_category", label: "Тип устройства" },
      { key: "os_root", label: "Семейство ОС" },
      { key: "operating_system", label: "ОС (детально)" },
      { key: "browser", label: "Браузер" },
      { key: "browser_language", label: "Язык браузера" },
      { key: "screen_format", label: "Формат экрана" },
    ],
  },
  {
    group: "Источник трафика",
    items: [
      { key: "last_traffic_source", label: "Тип источника" },
      { key: "last_adv_engine", label: "Рекламная сеть" },
      { key: "last_direct_click_order_name", label: "Кампания Я.Директ" },
      { key: "last_search_engine", label: "Поисковик" },
      { key: "last_search_phrase", label: "Поисковая фраза" },
      { key: "last_social_network", label: "Соцсеть" },
      { key: "last_referal_source", label: "Реферальный источник" },
    ],
  },
  {
    group: "Поведение",
    items: [
      { key: "start_url", label: "Страница входа" },
      { key: "end_url", label: "Страница выхода" },
      { key: "referer", label: "Реферрер" },
    ],
  },
];

export const fetchAudience = (campaignId: number | null, days: number, dimension: AudienceDimensionKey = "gender_age") =>
  api<AudienceRow[]>(`/api/analytics/audience?days=${days}&dimension=${dimension}${campaignId ? `&campaign_id=${campaignId}` : ""}`);

export const fetchLandings = (campaignId: number | null, days: number) =>
  api<LandingPerformance[]>(`/api/analytics/landings?days=${days}${campaignId ? `&campaign_id=${campaignId}` : ""}`);

export const fetchHourPatterns = (days: number) =>
  api<HourPattern[]>(`/api/analytics/hour-patterns?days=${days}`);

export const fetchBuyoutRate = (days: number, grouping: "global"|"category" = "global") =>
  api<BuyoutRate[]>(`/api/analytics/buyout-rate?days=${days}&grouping=${grouping}`);
